import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { Segmented } from "@dl/web-core-js/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NexpimView } from "../../views";
import { ImportInvoiceGrid } from "./ImportInvoicePage";
import { PpcSalesGrid } from "./PpcSalesInvoicePage";
import { ShippingInvoiceGrid } from "./ShippingInvoicePage";

type Kind = "import" | "shipping" | "ppc";

/**
 * The three manual invoice screens, as one page with a switch.
 *
 * They were three nav entries and three page shells for what is one question
 * asked of three feeds. Merging their rows into a single table was the obvious
 * move and the wrong one: import and PPC share seven columns, but shipping
 * carries a shipping invoice, a ship date and a recipient address on top, so a
 * shared table would be six mostly-empty columns wide. The switch keeps each
 * set of columns honest and still costs one nav entry instead of three.
 *
 * The three feeds are one permission, NEXPIM_MANUAL_INVOICES: the page is the
 * unit of access, so the switch always offers all three and the route guard in
 * the nav manifest is the only check.
 */
export function ManualInvoicesPage() {
  const { t } = useTranslation();
  const { hasView } = useAuth();
  const [kind, setKind] = useState<Kind>("import");
  const allowed = hasView(NexpimView.MANUAL_INVOICES);

  const options = [
    { value: "import" as const, label: t("manual-invoice.kind.import", "Import") },
    { value: "shipping" as const, label: t("manual-invoice.kind.shipping", "Shipping") },
    { value: "ppc" as const, label: t("manual-invoice.kind.ppc", "PPC") },
  ];

  const toolbar = <Segmented value={kind} onChange={setKind} options={options} />;

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.manual-invoices", "Manual invoices")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  const SUBTITLES: Record<Kind, string> = {
    import: t("manual-invoice.import.subtitle", "Expense invoices imported from supplier feeds."),
    shipping: t("manual-invoice.shipping.subtitle", "Shipping lines from imported carrier invoices."),
    ppc: t("manual-invoice.ppc.subtitle", "Advertising sales from imported platform invoices."),
  };

  return (
    <>
      <PageHeader
        title={t("navigation.item.manual-invoices", "Manual invoices")}
        subtitle={SUBTITLES[kind]}
      />
      {kind === "import" && <ImportInvoiceGrid toolbar={toolbar} />}
      {kind === "shipping" && <ShippingInvoiceGrid toolbar={toolbar} />}
      {kind === "ppc" && <PpcSalesGrid toolbar={toolbar} />}
    </>
  );
}
